/**
 * LoanFundSettings.tsx — 설정 › 대출 자금 종류 (2026-10-27 지도사 「위 트리를 내가 설정에서 변경할 수도 있어야 해 — 매년 사업명이 바뀔 거거든」)
 *  신청할 곳(중진공 · 소진공 · 신보 …)마다
 *   · 용도(운전자금 · 시설자금) — 비우면 묻지 않음
 *   · 자금 종류 트리 — 이름 · 아래 칸(구조개선전용 › 일반 · 선제적) · 진행 단계(비우면 위 칸 단계 → 그것도 없으면 기본 단계)
 *  대출 점검 카드의 「어떤 자금인가요?」가 이 목록으로 나온다. 고쳐도 이미 고른 업무는 그대로.
 */
import { useEffect, useMemo, useState } from 'react';
import type { CSSProperties } from 'react';
import { C, T, R, Btn } from './ui';
import { req } from '../api';

interface FundNode { name: string; steps?: string[]; children?: FundNode[] }
interface FundCfg { purposes: string[]; tree: FundNode[]; steps?: string[] }
/** steps = 코드 기본 단계 · defaultSteps = 지금 쓰는 기본 단계(설정에 적은 것 먼저 · 2026-10-30) */
interface BranchInfo { key: string; label: string; full: string; steps: string[]; defaultSteps?: string[] }
interface Loaded { branches: BranchInfo[]; trees: Record<string, FundCfg>; limit: { depth: number; nodes: number; name: number; steps: number; purposes: number } }

const inp: CSSProperties = { padding: '7px 10px', borderRadius: R.sm, border: `1px solid ${C.border}`, fontSize: T.sm, outline: 'none', boxSizing: 'border-box', minWidth: 0, fontFamily: 'inherit' };
const mini: CSSProperties = { height: 30, minWidth: 30, padding: '0 8px', borderRadius: R.sm, border: `1px solid ${C.border}`, background: C.white, color: C.sub, fontSize: T.xs, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap' };

/** 트리 안 한 칸을 길(번호 목록)로 바꾸기 — 바꾼 새 트리 */
function update(tree: FundNode[], at: number[], fn: (list: FundNode[], i: number) => FundNode[]): FundNode[] {
  if (at.length === 1) return fn(tree.slice(), at[0]);
  const [h, ...rest] = at;
  return tree.map((n, i) => (i === h ? { ...n, children: update(n.children || [], rest, fn) } : n));
}
const count = (list: FundNode[]): number => list.reduce((a, n) => a + 1 + count(n.children || []), 0);

export default function LoanFundSettings() {
  const [data, setData] = useState<Loaded | null>(null);
  const [key, setKey] = useState('kosmes');
  const [cfg, setCfg] = useState<FundCfg>({ purposes: [], tree: [] });
  const [dirty, setDirty] = useState(false);
  const [openSteps, setOpenSteps] = useState<string>('');   // 단계를 펼친 칸(길 문자열)
  const [newPurpose, setNewPurpose] = useState('');
  // 신청할 곳 기본 단계(한 줄에 하나) — 자금을 안 골랐거나 고른 자금에 단계가 없을 때 · 대출 점검 「이 순서를 기본 단계로」와 같은 값(2026-10-30)
  const [baseText, setBaseText] = useState('');
  const [msg, setMsg] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    req<Loaded>('GET', '/loan-fund-tree').then(r => { setData(r.data); }).catch((e: unknown) => setMsg(e instanceof Error ? e.message : '불러오지 못했어요'));
  }, []);
  useEffect(() => {
    if (!data) return;
    const c = data.trees[key];
    setCfg(c ? { purposes: c.purposes.slice(), tree: JSON.parse(JSON.stringify(c.tree)) as FundNode[] } : { purposes: [], tree: [] });
    setBaseText(((c && c.steps) || []).join('\n'));
    setDirty(false); setOpenSteps(''); setMsg('');
  }, [data, key]);
  const branch = useMemo(() => (data ? data.branches.find(b => b.key === key) : undefined), [data, key]);
  if (!data) return <div style={{ padding: 20, fontSize: T.sm, color: msg ? C.red : C.muted }}>{msg || '불러오는 중…'}</div>;
  const L = data.limit;

  const change = (next: FundCfg) => { setCfg(next); setDirty(true); setMsg(''); };
  const setTree = (tree: FundNode[]) => change({ ...cfg, tree });
  const pick = (k: string) => { if (k === key) return; if (dirty) { setMsg('저장하지 않은 내용이 있어요 — 먼저 저장하거나 「되돌리기」를 눌러 주세요'); return; } setKey(k); };
  async function save() {
    setSaving(true); setMsg('');
    try {
      const steps = baseText.split('\n').map(x => x.replace(/\s+/g, ' ').trim()).filter(Boolean);
      const r = await req<FundCfg & { defaultSteps?: string[] }>('PUT', `/loan-fund-tree/${key}`, { ...cfg, steps });
      setData(d => (d ? { ...d, trees: { ...d.trees, [key]: r.data }, branches: d.branches.map(b => (b.key === key ? { ...b, defaultSteps: r.data.defaultSteps || b.defaultSteps } : b)) } : d));
      setMsg('저장했어요 — 대출 점검 「어떤 자금인가요?」에 바로 나와요 · 이미 고른 업무는 그대로예요');
    } catch (e: unknown) { setMsg(e instanceof Error ? e.message : '저장하지 못했어요'); }
    finally { setSaving(false); }
  }
  const total = count(cfg.tree);

  /** 한 칸(+ 아래 칸) */
  const row = (n: FundNode, at: number[], siblings: number) => {
    const id = at.join('.');
    const i = at[at.length - 1];
    const depth = at.length;
    const stepsOpen = openSteps === id;
    const stepN = (n.steps || []).length;
    return (
      <div key={id} style={{ marginTop: 6 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'wrap' }}>
          <input value={n.name} maxLength={L.name} aria-label="자금 이름" placeholder="자금 이름"
            onChange={e => setTree(update(cfg.tree, at, (list, k) => { list[k] = { ...list[k], name: e.target.value }; return list; }))}
            style={{ ...inp, flex: '1 1 180px', fontWeight: depth === 1 ? 700 : 500 }} />
          <button type="button" style={{ ...mini, color: stepN ? C.accent : C.sub, borderColor: stepsOpen ? C.accent : C.border }} aria-expanded={stepsOpen}
            onClick={() => setOpenSteps(stepsOpen ? '' : id)} title="이 자금을 고르면 생기는 진행 단계">{stepN ? `단계 ${stepN}개` : '단계'} {stepsOpen ? '▴' : '▾'}</button>
          {depth < L.depth && <button type="button" style={mini} title="아래 칸 더하기 — 예) 구조개선전용 › 일반 · 선제적"
            onClick={() => setTree(update(cfg.tree, at, (list, k) => { list[k] = { ...list[k], children: (list[k].children || []).concat({ name: '' }) }; return list; }))}>＋ 아래 칸</button>}
          <button type="button" style={mini} aria-label="위로" disabled={i === 0} onClick={() => setTree(update(cfg.tree, at, (list, k) => { [list[k - 1], list[k]] = [list[k], list[k - 1]]; return list; }))}>↑</button>
          <button type="button" style={mini} aria-label="아래로" disabled={i === siblings - 1} onClick={() => setTree(update(cfg.tree, at, (list, k) => { [list[k + 1], list[k]] = [list[k], list[k + 1]]; return list; }))}>↓</button>
          <button type="button" style={{ ...mini, color: C.red }} aria-label={`${n.name || '이 칸'} 지우기`}
            onClick={() => { setOpenSteps(''); setTree(update(cfg.tree, at, (list, k) => { list.splice(k, 1); return list; })); }}>✕</button>
        </div>
        {stepsOpen && (
          <div style={{ margin: '6px 0 2px', padding: 10, borderRadius: R.sm, background: C.bgAlt, border: `1px solid ${C.border}` }}>
            <div style={{ fontSize: T.xs, fontWeight: 700, color: C.sub, marginBottom: 4 }}>진행 단계 <span style={{ fontWeight: 400, color: C.muted }}>· 한 줄에 하나 · 최대 {L.steps}개</span></div>
            <textarea rows={Math.max(3, stepN + 1)} value={(n.steps || []).join('\n')}
              placeholder={`비우면 ${depth > 1 ? '위 칸 단계 · 그것도 없으면 ' : ''}기본 단계(${branch ? branch.steps.join(' › ') : ''})`}
              onChange={e => setTree(update(cfg.tree, at, (list, k) => { list[k] = { ...list[k], steps: e.target.value.split('\n').slice(0, L.steps) }; return list; }))}
              style={{ ...inp, width: '100%', lineHeight: 1.6, resize: 'vertical' }} />
            <div style={{ fontSize: T.xs, color: C.muted, marginTop: 4 }}>업무에 아직 끝낸 단계가 없을 때 이 단계로 바뀌어요 · 대표님 · 팀장님 화면에도 이 이름으로 보여요</div>
          </div>
        )}
        {(n.children || []).length > 0 && (
          <div style={{ marginLeft: 14, paddingLeft: 12, borderLeft: `2px solid ${C.border}` }}>
            {(n.children || []).map((c, k) => row(c, at.concat(k), (n.children || []).length))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div style={{ padding: 20, maxWidth: 760 }}>
      <div style={{ marginBottom: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
          <span style={{ fontSize: T.xl }}>🏦</span><span style={{ fontSize: T.xl, fontWeight: 700, color: C.text }}>대출 자금 종류</span>
        </div>
        <div style={{ fontSize: T.xs, color: C.muted }}>대출 점검에서 신청할 곳을 고른 뒤 「어떤 자금인가요?」에 나오는 목록이에요. 해마다 사업명이 바뀌면 여기서 고쳐요. 고쳐도 이미 고른 업무는 그대로예요.</div>
      </div>

      {/* 신청할 곳 */}
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 18 }}>
        {data.branches.map(b => {
          const on = b.key === key, tr = data.trees[b.key], has = !!(tr && (tr.tree.length || (tr.steps || []).length));   // 자금 종류나 기본 단계를 정한 곳
          return <button key={b.key} type="button" aria-pressed={on} onClick={() => pick(b.key)}
            style={{ height: 32, padding: '0 14px', borderRadius: 16, border: `1px solid ${on ? C.primary : C.border}`, background: on ? C.primary : C.white, color: on ? C.white : has ? C.text : C.muted, fontSize: T.sm, fontWeight: on ? 700 : 500, cursor: 'pointer', fontFamily: 'inherit' }}>
            {b.label}{has ? '' : ' · 없음'}</button>;
        })}
      </div>

      {/* 기본 단계 — 신청할 곳마다(2026-10-30 지도사 「필요정보 수집을 제일 앞에 · 연관된 모든 모듈에」) */}
      <div style={{ marginBottom: 18 }}>
        <div style={{ fontSize: T.xs, fontWeight: 700, color: C.sub, marginBottom: 6 }}>기본 단계 <span style={{ fontWeight: 400, color: C.muted }}>· 한 줄에 하나 · 최대 {L.steps}개 · 자금을 안 고르거나 고른 자금에 단계가 없을 때 이 순서로 시작해요</span></div>
        <textarea rows={Math.max(3, baseText.split('\n').length + 1)} value={baseText} aria-label="기본 단계"
          placeholder={`비우면 ${branch ? branch.steps.join(' › ') : '처음 기본값'}`}
          onChange={e => { setBaseText(e.target.value.split('\n').slice(0, L.steps).join('\n')); setDirty(true); setMsg(''); }}
          style={{ ...inp, width: '100%', lineHeight: 1.6, resize: 'vertical' }} />
        <div style={{ fontSize: T.xs, color: C.muted, marginTop: 4 }}>대출 점검 카드의 「이 순서를 기본 단계로」도 여기에 저장돼요 · 고쳐도 이미 진행 중인 업무는 그대로예요</div>
      </div>

      {/* 용도 */}
      <div style={{ marginBottom: 18 }}>
        <div style={{ fontSize: T.xs, fontWeight: 700, color: C.sub, marginBottom: 6 }}>용도 <span style={{ fontWeight: 400, color: C.muted }}>· 비우면 묻지 않아요</span></div>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
          {cfg.purposes.map(p => (
            <span key={p} style={{ display: 'inline-flex', alignItems: 'center', gap: 2, height: 30, padding: '0 4px 0 12px', borderRadius: 15, border: `1px solid ${C.border}`, background: C.white, fontSize: T.sm, fontWeight: 600 }}>
              {p}<button type="button" aria-label={`${p} 빼기`} onClick={() => change({ ...cfg, purposes: cfg.purposes.filter(x => x !== p) })} style={{ border: 'none', background: 'none', color: C.muted, cursor: 'pointer', fontSize: T.sm, padding: '0 6px' }}>✕</button>
            </span>
          ))}
          {cfg.purposes.length < L.purposes && <span style={{ display: 'inline-flex', gap: 4 }}>
            <input value={newPurpose} onChange={e => setNewPurpose(e.target.value)} maxLength={L.name} placeholder="예) 운전자금"
              onKeyDown={e => { if (e.key === 'Enter' && newPurpose.trim() && !cfg.purposes.includes(newPurpose.trim())) { change({ ...cfg, purposes: cfg.purposes.concat(newPurpose.trim()) }); setNewPurpose(''); } }}
              style={{ ...inp, width: 140, height: 30, padding: '0 10px' }} />
            <button type="button" style={mini} disabled={!newPurpose.trim() || cfg.purposes.includes(newPurpose.trim())}
              onClick={() => { change({ ...cfg, purposes: cfg.purposes.concat(newPurpose.trim()) }); setNewPurpose(''); }}>＋ 더하기</button>
          </span>}
        </div>
      </div>

      {/* 자금 종류 */}
      <div style={{ marginBottom: 18 }}>
        <div style={{ fontSize: T.xs, fontWeight: 700, color: C.sub, marginBottom: 2 }}>자금 종류 <span style={{ fontWeight: 400, color: C.muted }}>· {total}/{L.nodes} · 「＋ 아래 칸」으로 나눠요(예: 구조개선전용 › 일반 · 선제적)</span></div>
        {cfg.tree.length === 0 && <div style={{ fontSize: T.sm, color: C.muted, padding: '10px 0' }}>아직 없어요 — 없으면 대출 점검에서 묻지 않아요</div>}
        {cfg.tree.map((n, k) => row(n, [k], cfg.tree.length))}
        <button type="button" style={{ ...mini, marginTop: 10, color: C.accent, borderColor: C.accent }} disabled={total >= L.nodes}
          onClick={() => setTree(cfg.tree.concat({ name: '' }))}>＋ 자금 종류 더하기</button>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 8, paddingTop: 14, borderTop: `1px solid ${C.border}`, flexWrap: 'wrap' }}>
        <Btn onClick={() => void save()} disabled={saving || !dirty}>{saving ? '저장 중' : '저장'}</Btn>
        {dirty && <Btn variant="secondary" onClick={() => { const c = data.trees[key]; setCfg(c ? { purposes: c.purposes.slice(), tree: JSON.parse(JSON.stringify(c.tree)) as FundNode[] } : { purposes: [], tree: [] }); setBaseText(((c && c.steps) || []).join('\n')); setDirty(false); setMsg(''); }}>되돌리기</Btn>}
        {msg && <span style={{ fontSize: T.xs, color: /못|없는|있어요 —|두 번|까지/.test(msg) ? C.red : C.sub }}>{msg}</span>}
      </div>
    </div>
  );
}
